import React, { useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";

type Props = {
  /** progress 0..100 */
  progress: number;
  /** diameter in px (default 120) */
  size?: number;
  /** stroke width in px (default 12) */
  strokeWidth?: number;
  /** animation duration in ms (default 800) */
  duration?: number;
  /** tailwind color for stroke, e.g. 'text-blue-500' */
  colorClass?: string;
  /** show percentage label */
  showLabel?: boolean;
  /** optional className for wrapper */
  className?: string;

  textStyle?: string

  suffix?: string
};

export default function AnimatedCircleProgress({
  progress,
  size = 48,
  strokeWidth = 5,
  duration = 800,
  colorClass = "text-green-500",
  showLabel = true,
  className = "",
  suffix,
  textStyle
}: Props) {
  const [animated, setAnimated] = useState(0);

  // clamp progress between 0 and 100
  const clamped = Math.max(0, Math.min(100, progress));

  useEffect(() => {
    // animate to new progress; using a small timeout ensures transition works on mount
    const raf = requestAnimationFrame(() => setAnimated(clamped));
    return () => cancelAnimationFrame(raf);
  }, [clamped]);

  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const dash = (animated / 100) * circumference;
  const offset = circumference - dash;

  // stroke-linecap styling: round ends
  const viewBox = `0 0 ${size} ${size}`;

  return (
    <div
      className={`inline-flex items-center justify-center ${className}`}
      style={{ width: size, height: size }}
      role="img"
      aria-label={`Progress ${clamped}%`}
    >
      <svg className="block" width={size} height={size} viewBox={viewBox}>
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={strokeWidth}
          className="text-gray-200"
          stroke="currentColor"
          fill="transparent"
        />

        {/* Foreground (progress) circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          stroke="currentColor"
          className={`${colorClass}`}
          fill="transparent"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{
            transform: `rotate(-90deg)`,
            transformOrigin: "50% 50%",
            transition: `stroke-dashoffset ${duration}ms ease, stroke ${duration}ms ease`,
          }}
        />
      </svg>

      {/* Center label */}
      {showLabel && (
        <div
          className="absolute pointer-events-none text-sm font-semibold"
          style={{
            width: size - strokeWidth * 2,
            textAlign: "center",
          }}
        >
          <span className={twMerge(`select-none text-light-black text-xs font-medium`, textStyle)}>{Math.round(animated)}{suffix}</span>
        </div>
      )}
    </div>
  );
}
